import React, { useContext, useMemo } from "react"
import { AdminContext } from "~/contexts/AdminContext"
import { Cog6ToothIcon } from "@heroicons/react/20/solid"
import DropdownButton, { DropdownOption } from "~/components/ui/Admin/DropdownButton"
import Toggle from "~/components/ui/Toggle"
import { CampingStyleProps, NewReservationProps } from "~/interfaces"
import ePaymentType, { PaymentType } from "~/enums/ePaymentType"
import classNames from "~/utils/classNames"
import PortalHoverTip from "~/components/ui/PortalHoverTip"

export type ToggleFeesTypes = Pick<
  NewReservationProps,
  | "is_tax_enabled"
  | "is_credit_card_fee_enabled"
  | "is_weekend_fee_enabled"
  | "is_holiday_rate_enabled"
  | "is_pets_fee_enabled"
  | "is_passenger_fees_enabled"
>

export type OptionKeys = "taxRate" | "petsFee" | "holidayRate" | "weekendFee" | "passengerFee" | "creditCard"

interface FeesAndTaxesSettingsProps {
  enabledFees: Partial<Record<keyof ToggleFeesTypes, boolean>> | undefined
  options: OptionKeys[]
  setValue: (key: keyof ToggleFeesTypes, value: boolean) => void
  campingStyle: CampingStyleProps | undefined
  isGroup?: boolean
  isStore?: boolean
  isLongTermElectric?: boolean
  isPartiallyPaid?: boolean
  paymentMethod?: PaymentType
  className?: string
  isAcceptingDeposit?: boolean
  isInvoice?: boolean
  placement?: "left" | "right"
}

const FeesAndTaxesSettings = ({
  enabledFees,
  options,
  setValue,
  campingStyle,
  isGroup,
  isStore,
  isLongTermElectric,
  isPartiallyPaid,
  paymentMethod,
  className,
  isAcceptingDeposit,
  isInvoice,
  placement = "right"
}: FeesAndTaxesSettingsProps) => {
  const { camp } = useContext(AdminContext)

  if (!enabledFees) return <FeesAndTaxesSkeleton />
  const optionConfig: Record<
    OptionKeys,
    {
      label: string
      key: keyof ToggleFeesTypes
      isAvailable: boolean | undefined
      disabled?: boolean
      tooltip?: string
    }
  > = {
    taxRate: {
      label: "Add tax",
      key: "is_tax_enabled",
      isAvailable: !!(campingStyle?.tax_rate ?? (isStore ? camp?.tax_rate || camp?.store_tax_rate : camp?.tax_rate)),
      disabled: isPartiallyPaid || isLongTermElectric,
      tooltip: isPartiallyPaid ? "Changing tax options is disabled for partial payments"
        : isAcceptingDeposit ? "Tax will be applied at time of full payment"
          : "",
    },
    creditCard: {
      label: "Add credit card fee",
      key: "is_credit_card_fee_enabled",
      isAvailable: !!campingStyle?.credit_card_rate,
      disabled: (paymentMethod && paymentMethod === ePaymentType.LINK) || isInvoice,
      tooltip: (paymentMethod && paymentMethod === ePaymentType.LINK)
        ? "Credit card processing fees are applied at the time of payment based on the payment method"
        : isInvoice
          ? "Credit Card fees are now applied automatically at checkout if a guest pays online."
          : "",
    },
    holidayRate: {
      label: "Add holiday rate",
      key: "is_holiday_rate_enabled",
      isAvailable: !!(campingStyle?.holiday_fee_in_cents ?? camp.holiday_fee_in_cents),
    },
    weekendFee: {
      label: "Add weekend fee",
      key: "is_weekend_fee_enabled",
      isAvailable: !!(campingStyle?.weekend_fee_in_cents ?? camp.weekend_fee_in_cents),
    },
    passengerFee: {
      label: "Add passenger fee",
      key: "is_passenger_fees_enabled",
      isAvailable: !isGroup,
    },
    petsFee: {
      label: "Add pets fee",
      key: "is_pets_fee_enabled",
      isAvailable: !isGroup,
    },
  }

  const buildDropdownOptions = (): DropdownOption[] => {
    return options
      .filter((key) => optionConfig[key].isAvailable)
      .map((key) => {
        const config = optionConfig[key]
        const tooltip = config.tooltip || ""
        return {
          label: (
            <PortalHoverTip content={tooltip}>
              <div
                className={`w-52 flex justify-between items-center whitespace-nowrap ${config.disabled && "cursor-not-allowed pointer-events-none"
                  }`}
              >
                <div className={`text-sm md:text-base ${config.disabled ? "text-gray-400" : ""}`}>{config.label}</div>
                <Toggle
                  disabled={config.disabled}
                  checked={enabledFees[config.key] as boolean}
                  onChange={(value) => {
                    if (!config.disabled) setValue(config.key, value)
                  }}
                />
              </div>
            </PortalHoverTip>
          ),
          onClick: () => {
            if (!config.disabled) setValue(config.key, !enabledFees[config.key])
          },
        }
      })
  }

  const dropdownOptions = buildDropdownOptions()
  const enabledFeesCount = useMemo(() => {
    return options
      .filter((optionKey) => optionConfig[optionKey].isAvailable)
      .filter((optionKey) => !!enabledFees[optionConfig[optionKey].key]).length
  }, [options, optionConfig, enabledFees])

  return (
    <div className={classNames("w-full flex justify-end mb-2", className)}>
      {dropdownOptions.length > 1 ? (
        <DropdownButton
          autoWidth
          preventCloseOnClick
          options={dropdownOptions}
          placement={placement}
          verticalPlacement="top"
          isMenuFixed
          minWidth={160}
        >
          <div className="flex items-center rounded-lg p-2 transition-colors duration-200 hover:bg-gray-100 text-gray-600 cursor-pointer relative">
            Fees & taxes <Cog6ToothIcon className="ml-2 w-6 h-6" />
            {!!enabledFeesCount && (
              <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-yellow-400 text-black text-xs font-medium">
                {enabledFeesCount}
              </span>
            )}
          </div>
        </DropdownButton>
      ) : (
        dropdownOptions.map((option, index) => (
          <div
            key={index}
            className="flex items-center rounded-md p-2 text-gray-600"
            onClick={(e) => e.stopPropagation()}
          >
            {option.label}
          </div>
        ))
      )}
    </div>
  )
}

export default FeesAndTaxesSettings

const FeesAndTaxesSkeleton = () => {
  return (
    <div className="w-full flex justify-end mb-2">
      <div className="flex items-center rounded-md p-2 transition-colors duration-100 bg-gray-100 text-gray-400 cursor-not-allowed">
        <div className="flex items-center gap-x-2">
          <div className="h-4 w-4 bg-gray-300 rounded-full animate-pulse"></div>
          <div className="h-4 w-20 bg-gray-300 rounded-md animate-pulse"></div>
        </div>
        <Cog6ToothIcon className="ml-2 w-6 h-6 text-gray-300" />
      </div>
    </div>
  )
}
